import React, { useMemo, useState } from 'react'

interface Point {
  month: string
  investment: number
  profit: number
  total?: number
}

interface Props {
  data: Point[]
  height?: number
  barWidth?: number
  groupGap?: number
  labelEvery?: number
  currency: (n: number) => string
  labels?: {
    investment?: string
    profit?: string
    total?: string
  }
}

function niceMax(value: number): { max: number; step: number } {
  if (value <= 0) return { max: 1, step: 0.2 }
  const exp = Math.floor(Math.log10(value))
  const base = Math.pow(10, exp)
  const n = value / base
  let nice = 10
  if (n <= 1) nice = 1
  else if (n <= 2) nice = 2
  else if (n <= 5) nice = 5
  const max = nice * base
  const step = max / 5
  return { max, step }
}

// Compact axis-tick formatter (no cents — grid values are already "nice" round numbers)
function axisLabel(v: number): string {
  if (v === 0) return '0 €'
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(v) + ' €'
}

export const BarGroupChart: React.FC<Props> = ({
  data,
  height = 260,
  barWidth = 10,
  groupGap = 18,
  labelEvery,
  currency,
  labels,
}) => {
  const margin = { top: 16, right: 16, bottom: 36, left: 68 }
  const groups = data.length

  // Adaptive spacing for small ranges (6/12 ay daha ferah görünsün)
  const baseGroupWidth = barWidth * 2 + 6 // 2 bars + inner gap
  const pxPerGroup = groups <= 6 ? 100 : groups <= 12 ? 70 : 40
  const adaptiveGap = Math.max(10, pxPerGroup - baseGroupWidth)
  const groupWidth = baseGroupWidth
  const width = margin.left + margin.right + groups * (groupWidth + adaptiveGap) - adaptiveGap

  const maxY = useMemo(() => {
    return Math.max(1, ...data.map(d => Math.max(d.investment, d.profit)))
  }, [data])
  const { max, step } = niceMax(maxY)

  // label density
  const autoEvery = useMemo(() => {
    if (groups >= 24) return 3
    if (groups >= 16) return 2
    return 1
  }, [groups])
  const showEvery = labelEvery || autoEvery

  const yScale = (v: number) => {
    const h = height - margin.top - margin.bottom
    return margin.top + h - (v / max) * h
  }

  const formatMonth = (m: string) => m

  const [tooltip, setTooltip] = useState<null | { x: number; y: number; d: Point }>(null)

  const t = {
    investment: labels?.investment || 'Yatırım',
    profit: labels?.profit || 'Kar',
    total: labels?.total || 'Toplam',
  }

  const chartWidth = Math.max(width, 720)

  return (
    <div className="relative">
      {/* Legend */}
      <div className="flex items-center gap-5 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-primary-600 shrink-0" />
          <span className="text-xs font-medium text-slate-600">{t.investment}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-success-600 shrink-0" />
          <span className="text-xs font-medium text-slate-600">{t.profit}</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <svg width={chartWidth} height={height} className="block">
          {/* Grid lines */}
          {[0, step, step * 2, step * 3, step * 4, step * 5].map((v, i) => (
            <g key={i}>
              <line
                x1={margin.left}
                x2={chartWidth - margin.right}
                y1={yScale(v)}
                y2={yScale(v)}
                className="stroke-slate-200"
                strokeDasharray="4 4"
              />
              <text
                x={margin.left - 10}
                y={yScale(v)}
                textAnchor="end"
                alignmentBaseline="middle"
                className="fill-slate-500 text-[10px] tabular-nums"
              >
                {axisLabel(v)}
              </text>
            </g>
          ))}

          {/* Bars */}
          {data.map((d, idx) => {
            const gx = margin.left + idx * (groupWidth + adaptiveGap)
            const invH = Math.max(yScale(0) - yScale(d.investment), 1)
            const prfH = Math.max(yScale(0) - yScale(d.profit), 1)
            const cx = gx + groupWidth / 2
            return (
              <g key={idx}>
                {/* investment */}
                <rect
                  x={gx}
                  y={yScale(d.investment)}
                  width={barWidth}
                  height={invH}
                  className="fill-primary-600"
                  onMouseEnter={() => setTooltip({ x: cx, y: yScale(Math.max(d.investment, d.profit)), d })}
                  onMouseMove={() => setTooltip({ x: cx, y: yScale(Math.max(d.investment, d.profit)), d })}
                  onMouseLeave={() => setTooltip(null)}
                />
                {/* profit */}
                <rect
                  x={gx + barWidth + 6}
                  y={yScale(d.profit)}
                  width={barWidth}
                  height={prfH}
                  className="fill-success-600"
                  onMouseEnter={() => setTooltip({ x: cx, y: yScale(Math.max(d.investment, d.profit)), d })}
                  onMouseMove={() => setTooltip({ x: cx, y: yScale(Math.max(d.investment, d.profit)), d })}
                  onMouseLeave={() => setTooltip(null)}
                />
                {/* X labels */}
                {idx % showEvery === 0 && (
                  <g>
                    <text
                      x={cx}
                      y={height - margin.bottom + 14}
                      textAnchor="middle"
                      className="fill-slate-600 text-[10px]"
                    >
                      {formatMonth(d.month)}
                    </text>
                    <text
                      x={cx}
                      y={height - margin.bottom + 26}
                      textAnchor="middle"
                      className="fill-slate-400 text-[10px] tabular-nums"
                    >
                      {currency(d.total || 0)}
                    </text>
                  </g>
                )}
              </g>
            )
          })}
        </svg>
      </div>
      {tooltip && (
        <div
          className="absolute bg-white border border-slate-200 border-t-2 border-t-primary-600 px-3 py-2 text-[12px] pointer-events-none"
          style={{ left: tooltip.x + 10, top: tooltip.y + 10 }}
        >
          <div className="font-medium text-slate-900">{tooltip.d.month}</div>
          <div className="text-primary-600">{t.investment}: {currency(tooltip.d.investment)}</div>
          <div className="text-success-600">{t.profit}: {currency(tooltip.d.profit)}</div>
          <div className="text-slate-600">{t.total}: {currency(tooltip.d.total || 0)}</div>
        </div>
      )}
    </div>
  )
}

export default BarGroupChart
